import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  HeartPulse,
  AtSign,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Coins,
  KeyRound,
  Mail,
  MapPin,
  MoreHorizontal,
  Pencil,
  ShieldOff,
  ShieldMinus,
  Phone,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  Users,
  ShieldCheck,
  UserRoundCheck,
  X,
} from 'lucide-react'
import { useAuthStore } from '@/store'
import {
  fetchManagementUsers,
  setUserAccessDisabled,
  updateManagementMemberProfile,
  type ManagementUser,
  type ManagementUserRole,
  type ManagementUserRoleGroup,
} from '@/services/managementUsersService'
import {
  activateManagementAccess,
  deactivateManagementAccess,
  demoteToStandard,
  promoteToAdvanced,
} from '@/services/managementAccessService'
import {
  JOB_TITLE_LABELS,
  ROYALTY_ROLE_LABELS,
  type AssignableJobTitle,
} from '@/services/managementRolesService'
import { ManagementAccessInbox } from '@/components/management/ManagementAccessInbox'
import { UserRolesDialog } from '@/components/management/UserRolesDialog'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import type { RoyaltyRole } from '@/types'

/** Ось членства в УК — отдельная от `users.access_disabled` (вход в приложение). */
type ManagementMemberAction = 'promote' | 'demote' | 'deactivate' | 'activate'

const MANAGEMENT_ACTION_RUNNERS: Record<ManagementMemberAction, (userId: string) => Promise<string>> = {
  promote: promoteToAdvanced,
  demote: demoteToStandard,
  deactivate: deactivateManagementAccess,
  activate: activateManagementAccess,
}

const ROLE_LABELS: Record<ManagementUserRole, string> = {
  management_company: 'УК',
  territorial_manager: 'Терр. управляющий',
  personal_manager: 'Личный менеджер',
  trainer: 'Педагогический отдел',
  owner: 'Владелец',
  manager: 'Менеджер',
  member: 'Участник',
  user: 'Без роли',
}

const GROUP_LABELS: Record<ManagementUserRoleGroup, string> = {
  management_company: 'Управляющая компания',
  owners: 'Владельцы',
  team: 'Команды студий',
  other: 'Остальные',
}

const GROUP_OPTIONS: Array<{ value: ManagementUserRoleGroup | 'all'; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'management_company', label: GROUP_LABELS.management_company },
  { value: 'owners', label: GROUP_LABELS.owners },
  { value: 'team', label: GROUP_LABELS.team },
  { value: 'other', label: GROUP_LABELS.other },
]

// Второй слой фильтров — «что не так со входом». Считаем по факту привязки в
// user_auth_providers, а не по users.telegram_id/users.email: вход ищет пользователя
// именно по провайдеру, поэтому «нет ТГ» здесь означает «не сможет войти телеграмом».
type GapFilter = 'no_telegram' | 'no_email' | 'email_unverified' | 'access_disabled'

const GAP_PREDICATES: Record<GapFilter, (user: ManagementUser) => boolean> = {
  no_telegram: user => !user.has_telegram_provider && user.telegram_id === null,
  no_email: user => !user.has_email_provider && !user.email,
  email_unverified: user => Boolean(user.email) && !user.email_verified_at,
  access_disabled: user => user.access_disabled,
}

const GAP_OPTIONS: Array<{ value: GapFilter; label: string }> = [
  { value: 'no_telegram', label: 'Нет Telegram' },
  { value: 'no_email', label: 'Нет email' },
  { value: 'email_unverified', label: 'Email не подтверждён' },
  { value: 'access_disabled', label: 'Доступ отключён' },
]

// Третий слой фильтров — «кто это внутри УК»: уровень доступа, должность и роль в
// контуре роялти. Оси независимые (супер-админом может быть standard-сотрудник,
// бухгалтером роялти — тоже), поэтому это три отдельных набора, а не один список ролей.
// Внутри набора отметки складываются по ИЛИ (значения взаимоисключающие), между
// наборами — по И, как у групп и пробелов входа.
type AccessFilter = 'advanced' | 'standard' | 'super_admin' | 'mc_inactive'

const ACCESS_PREDICATES: Record<AccessFilter, (user: ManagementUser) => boolean> = {
  advanced: user => user.mc_is_active && user.access_level === 'advanced',
  standard: user => user.mc_is_active && user.access_level === 'standard',
  super_admin: user => user.is_super_admin,
  // Строка доступа есть, но снята — такой человек в приложение входит, а в УК уже нет.
  mc_inactive: user => !user.mc_is_active && user.access_level !== null,
}

const ACCESS_OPTIONS: Array<{ value: AccessFilter; label: string }> = [
  { value: 'advanced', label: 'Расширенный' },
  { value: 'standard', label: 'Обычный' },
  { value: 'super_admin', label: 'Супер-админ' },
  { value: 'mc_inactive', label: 'Доступ УК снят' },
]

type JobFilter = AssignableJobTitle | 'other' | 'none'

const JOB_OPTIONS: Array<{ value: JobFilter; label: string }> = [
  { value: 'territorial_manager', label: 'ТУ' },
  { value: 'personal_manager', label: 'ЛМ' },
  { value: 'pedagogy_specialist', label: 'Спец. по педагогам' },
  { value: 'pedagogy_lead', label: 'Рук. педагогов' },
  { value: 'lawyer', label: 'Юрист' },
  { value: 'other', label: 'Другое' },
  { value: 'none', label: 'Без должности' },
]

function matchesJobFilter(user: ManagementUser, value: JobFilter): boolean {
  if (value === 'none') return user.mc_is_active && user.job_title === null
  return user.job_title === value
}

type RoyaltyFilter = RoyaltyRole | 'none'

const ROYALTY_OPTIONS: Array<{ value: RoyaltyFilter; label: string }> = [
  { value: 'head', label: ROYALTY_ROLE_LABELS.head },
  { value: 'accountant', label: ROYALTY_ROLE_LABELS.accountant },
  { value: 'super_admin', label: ROYALTY_ROLE_LABELS.super_admin },
  { value: 'territorial_manager', label: ROYALTY_ROLE_LABELS.territorial_manager },
  { value: 'none', label: 'Без роли' },
]

function matchesRoyaltyFilter(user: ManagementUser, value: RoyaltyFilter): boolean {
  if (value === 'none') return user.mc_is_active && user.royalty_role === null
  return user.royalty_role === value
}

/**
 * Что писать в бейдже роли. Классификация в RPC не знает про юриста и «другое»,
 * поэтому такой сотрудник выглядел бы просто как «УК»: должность важнее.
 */
function roleLabel(user: ManagementUser): string {
  if (user.mc_is_active && user.job_title) return JOB_TITLE_LABELS[user.job_title]
  return ROLE_LABELS[user.role] ?? user.role
}

function displayName(user: ManagementUser): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  if (name) return name
  if (user.tg_username) return `@${user.tg_username}`
  if (user.email) return user.email
  return 'Без имени'
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function confirmManagementAction(user: ManagementUser, action: ManagementMemberAction): boolean {
  const name = displayName(user)

  if (action === 'promote') {
    return confirm(
      `Повысить пользователя ${name} до расширенного доступа?\n\n` +
      'После повышения пользователю станет доступна чувствительная и финансовая информация в приложении.\n\n' +
      'Нажмите OK, чтобы подтвердить, или Отмена, чтобы отменить действие.'
    )
  }
  if (action === 'demote') {
    return confirm(
      `Вернуть пользователю ${name} обычный доступ УК?\n\n` +
      'Расширенные разделы и операции станут недоступны. Вход в приложение и обычный доступ УК сохранятся.'
    )
  }
  if (action === 'deactivate') {
    return confirm(
      `Снять доступ УК у пользователя ${name}?\n\n` +
      'Это не отключит вход в приложение целиком.'
    )
  }
  return confirm(`Вернуть доступ УК пользователю ${name}?`)
}

/**
 * Действия над доступом сотрудника в УК — то, что раньше жило на экране
 * «Управление доступом УК» внизу настроек. Набор ограничен тем, что умеют сами
 * RPC: повысить и снять можно только активного сотрудника со `standard`,
 * понизить — активного с `advanced`, вернуть — любого неактивного. Advanced
 * сначала возвращается к стандартному уровню и только затем может быть снят:
 * это сохраняет прежние серверные ограничения на деактивацию.
 */
function managementActionsFor(user: ManagementUser): ManagementMemberAction[] {
  if (user.access_level === null) return []
  if (!user.mc_is_active) return ['activate']
  return user.access_level === 'standard' ? ['promote', 'deactivate'] : ['demote']
}

function UserRow({
  user,
  currentUserId,
  busy,
  onAccessChange,
  onEditProfile,
  onManageRoles,
  onManagementAction,
  managementBusy,
}: {
  user: ManagementUser
  currentUserId: string | null
  busy: boolean
  onAccessChange: (user: ManagementUser) => void
  onEditProfile: (user: ManagementUser) => void
  onManageRoles: (user: ManagementUser) => void
  onManagementAction: (user: ManagementUser, action: ManagementMemberAction) => void
  managementBusy: boolean
}) {
  const managementActions = managementActionsFor(user)
  const actionMenuRef = useRef<HTMLDetailsElement>(null)
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)

  useEffect(() => {
    if (!isActionMenuOpen) return

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !actionMenuRef.current?.contains(event.target)) {
        setIsActionMenuOpen(false)
      }
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsActionMenuOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [isActionMenuOpen])

  const closeMenuAndRun = (action: () => void) => {
    setIsActionMenuOpen(false)
    action()
  }

  return (
    <div className={`border-b border-border px-3 py-3 last:border-b-0 ${user.access_disabled ? 'bg-red-50/60' : ''}`}>
      <div className="flex items-start gap-2 sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{displayName(user)}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
              {roleLabel(user)}
            </span>
            {user.access_disabled && (
              <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-700">
                <ShieldOff className="h-3 w-3" /> Доступ отключён
              </span>
            )}
            {user.mc_is_active && user.access_level && (
              <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                user.access_level === 'advanced'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {user.access_level === 'advanced' ? 'расширенный доступ' : 'обычный доступ'}
              </span>
            )}
            {user.is_super_admin && (
              <span className="inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700">
                <ShieldCheck className="h-3 w-3" /> супер-админ
              </span>
            )}
            {user.royalty_role && (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">
                <Coins className="h-3 w-3" /> роялти: {ROYALTY_ROLE_LABELS[user.royalty_role].toLowerCase()}
              </span>
            )}
            {/* Города доступных студий — у владельцев и команд (у УK доступ ко всей сети, тега нет) */}
            {user.studio_city_names.map((city, index) => (
              <span
                key={city}
                className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"
              >
                {index === 0 && <MapPin className="h-3 w-3" />}
                {city}
              </span>
            ))}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {user.tg_username && (
              <span className="inline-flex items-center gap-1">
                <AtSign className="h-3.5 w-3.5" />
                {user.tg_username}
              </span>
            )}
            {user.email && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                <span className="break-all">{user.email}</span>
                {user.email_verified_at ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-label="email подтверждён" />
                ) : (
                  <ShieldAlert className="h-3.5 w-3.5 text-amber-500" aria-label="email не подтверждён" />
                )}
              </span>
            )}
            {user.phone_number && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                {user.phone_number}
              </span>
            )}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              Способы входа:
              {user.has_telegram_provider && (
                <span className="inline-flex items-center gap-0.5 rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">
                  <Send className="h-3 w-3" /> Telegram
                </span>
              )}
              {user.has_email_provider && (
                <span className="inline-flex items-center gap-0.5 rounded bg-violet-50 px-1.5 py-0.5 text-violet-700">
                  <Mail className="h-3 w-3" /> Email
                </span>
              )}
              {!user.has_telegram_provider && !user.has_email_provider && <span>не привязаны</span>}
            </span>
            <span>Регистрация: {formatDate(user.created_at)}</span>
            <span>Последний визит: {formatDateTime(user.last_seen_at)}</span>
          </div>
          {user.access_disabled && (
            <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
              <span className="font-medium">Причина:</span> {user.access_disabled_reason}
              <span className="ml-2">
                {formatDateTime(user.access_disabled_at)}
                {user.access_disabled_by_name ? ` · ${user.access_disabled_by_name}` : ''}
              </span>
            </div>
          )}
        </div>
        <details
          ref={actionMenuRef}
          open={isActionMenuOpen}
          onToggle={event => setIsActionMenuOpen(event.currentTarget.open)}
          className="relative ml-auto shrink-0"
        >
          <summary
            className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted [&::-webkit-details-marker]:hidden"
            title={`Действия: ${displayName(user)}`}
            aria-label={`Действия: ${displayName(user)}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </summary>
          <div className="absolute right-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-md border border-border bg-card py-1 shadow-lg">
            <button
              type="button"
              onClick={() => closeMenuAndRun(() => onAccessChange(user))}
              disabled={busy || (!user.access_disabled && user.user_id === currentUserId)}
              title={!user.access_disabled && user.user_id === currentUserId ? 'Нельзя отключить собственный вход' : undefined}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {user.access_disabled ? <UserRoundCheck className="h-3.5 w-3.5 text-emerald-700" /> : <ShieldOff className="h-3.5 w-3.5 text-red-600" />}
              {user.access_disabled ? 'Включить вход в приложение' : 'Отключить вход в приложение'}
            </button>

            {user.access_level !== null && user.user_id !== currentUserId && (
              <button
                type="button"
                onClick={() => closeMenuAndRun(() => onEditProfile(user))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground hover:bg-muted"
              >
                <Pencil className="h-3.5 w-3.5" />
                Исправить имя и должность
              </button>
            )}

            {user.access_level !== null && (
              <button
                type="button"
                onClick={() => closeMenuAndRun(() => onManageRoles(user))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground hover:bg-muted"
              >
                <Briefcase className="h-3.5 w-3.5" />
                Роли и должность
              </button>
            )}

            {managementActions.length > 0 && <div className="my-1 border-t border-border" />}
            {managementActions.includes('promote') && (
              <button
                type="button"
                disabled={managementBusy}
                onClick={() => closeMenuAndRun(() => onManagementAction(user, 'promote'))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-50"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Расширить доступ УК
              </button>
            )}
            {managementActions.includes('demote') && (
              <button
                type="button"
                // Зеркало серверного гарда: понизить себя нельзя — расширенные
                // экраны закрылись бы посреди операции, а кнопка возврата живёт
                // на них же.
                disabled={managementBusy || user.user_id === currentUserId}
                title={user.user_id === currentUserId ? 'Нельзя понизить собственный доступ' : undefined}
                onClick={() => closeMenuAndRun(() => onManagementAction(user, 'demote'))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ShieldMinus className="h-3.5 w-3.5" />
                Вернуть обычный доступ УК
              </button>
            )}
            {managementActions.includes('deactivate') && (
              <button
                type="button"
                disabled={managementBusy || user.user_id === currentUserId}
                title={user.user_id === currentUserId ? 'Нельзя снять доступ УК самому себе' : undefined}
                onClick={() => closeMenuAndRun(() => onManagementAction(user, 'deactivate'))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ShieldOff className="h-3.5 w-3.5" />
                Снять доступ УК
              </button>
            )}
            {managementActions.includes('activate') && (
              <button
                type="button"
                disabled={managementBusy}
                onClick={() => closeMenuAndRun(() => onManagementAction(user, 'activate'))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                <UserRoundCheck className="h-3.5 w-3.5" />
                Вернуть доступ УК
              </button>
            )}
          </div>
        </details>
      </div>
    </div>
  )
}

/** Один набор ролевых отметок: подпись слева, чипы со счётчиками справа. */
function FilterChipRow<T extends string>({
  label,
  options,
  active,
  counts,
  onToggle,
}: {
  label: string
  options: Array<{ value: T; label: string }>
  active: T[]
  counts: Record<T, number>
  onToggle: (value: T) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-[104px] shrink-0 text-[11px] text-muted-foreground">{label}</span>
      {options.map(option => {
        const isActive = active.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onToggle(option.value)}
            className={`h-7 shrink-0 rounded-md border px-2 text-[11px] font-medium ${
              isActive
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-background text-muted-foreground hover:bg-muted'
            }`}
          >
            {option.label}
            <span className={`ml-1.5 ${isActive ? 'opacity-70' : 'text-muted-foreground/70'}`}>
              {counts[option.value]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export function ManagementUsersPage() {
  const navigate = useNavigate()
  const user = useAuthStore(s => s.user)
  const queryClient = useQueryClient()
  // Своя роль супер-админа — прямым ответом сервера (`is_super_admin()`), тем же, чем
  // закрыт маршрут: если ролевая выдача не загрузится, кнопки не должны объяснять
  // отказ «вы не супер-админ», когда дело в другом.
  const { isSuperAdmin } = useSuperAdmin()
  const [search, setSearch] = useState('')
  const [group, setGroup] = useState<ManagementUserRoleGroup | 'all'>('all')
  const [gaps, setGaps] = useState<GapFilter[]>([])
  const [accessFilters, setAccessFilters] = useState<AccessFilter[]>([])
  const [jobFilters, setJobFilters] = useState<JobFilter[]>([])
  const [royaltyFilters, setRoyaltyFilters] = useState<RoyaltyFilter[]>([])
  const [showMcFilters, setShowMcFilters] = useState(false)
  const [rolesTarget, setRolesTarget] = useState<ManagementUser | null>(null)
  const [accessTarget, setAccessTarget] = useState<ManagementUser | null>(null)
  const [accessReason, setAccessReason] = useState('')
  const [accessError, setAccessError] = useState<string | null>(null)
  const [profileTarget, setProfileTarget] = useState<ManagementUser | null>(null)
  const [profileFirstName, setProfileFirstName] = useState('')
  const [profileLastName, setProfileLastName] = useState('')
  const [profilePosition, setProfilePosition] = useState('')
  const [profileError, setProfileError] = useState<string | null>(null)
  const [managementNotice, setManagementNotice] = useState<string | null>(null)
  const [managementError, setManagementError] = useState<string | null>(null)

  const toggleGap = (value: GapFilter) =>
    setGaps(current => (current.includes(value) ? current.filter(v => v !== value) : [...current, value]))

  function toggleIn<T>(setter: (updater: (current: T[]) => T[]) => void, value: T) {
    setter(current => (current.includes(value) ? current.filter(v => v !== value) : [...current, value]))
  }

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['management-users'],
    queryFn: () => fetchManagementUsers({ limit: 1000 }),
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  })

  // Стабильная ссылка: без useMemo пустой массив-заглушка пересоздавался бы на каждый
  // рендер и обнулял все useMemo ниже (фильтрация и три набора счётчиков).
  const users = useMemo(() => data?.users ?? [], [data])
  const royaltyRolesAvailable = data?.royaltyRolesAvailable ?? false
  const superAdminFlagsAvailable = data?.superAdminFlagsAvailable ?? false

  /**
   * Права текущего администратора: уровень доступа и роль в роялти — из СВОЕЙ строки
   * этой же выдачи, а не из `getAccessLevel()` (тот кеширует уровень в localStorage без
   * TTL, и после смены прав экран рисовал бы кнопки по устаревшему снимку); роль
   * супер-админа — прямым ответом `is_super_admin()`, см. `useSuperAdmin` выше.
   */
  const actorRow = users.find(row => row.user_id === user?.id) ?? null
  const actorCapabilities = {
    isAdvanced: Boolean(actorRow?.mc_is_active && actorRow.access_level === 'advanced'),
    isSuperAdmin: isSuperAdmin === true,
    royaltyRole: actorRow?.royalty_role ?? null,
  }

  // Выдача и снятие доступа в УК: с 30.08.2026 это операция супер-админа, и
  // живёт она здесь, рядом со справочником, а не на экране личных настроек.
  const managementMutation = useMutation({
    mutationFn: ({ user: target, action }: { user: ManagementUser; action: ManagementMemberAction }) =>
      MANAGEMENT_ACTION_RUNNERS[action](target.user_id),
    onSuccess: async message => {
      setManagementNotice(message)
      setManagementError(null)
      await queryClient.invalidateQueries({ queryKey: ['management-users'] })
    },
    onError: (mutationError: unknown) => {
      setManagementNotice(null)
      setManagementError(
        mutationError instanceof Error ? mutationError.message : 'Не удалось изменить доступ в УК'
      )
    },
  })

  const accessMutation = useMutation({
    mutationFn: async () => {
      if (!accessTarget) return
      await setUserAccessDisabled({
        userId: accessTarget.user_id,
        disabled: !accessTarget.access_disabled,
        reason: accessReason,
      })
    },
    onSuccess: async () => {
      setAccessTarget(null)
      setAccessReason('')
      setAccessError(null)
      await queryClient.invalidateQueries({ queryKey: ['management-users'] })
    },
    onError: (mutationError: unknown) => {
      setAccessError(mutationError instanceof Error ? mutationError.message : 'Не удалось изменить доступ')
    },
  })

  const profileMutation = useMutation({
    mutationFn: async () => {
      if (!profileTarget) return
      await updateManagementMemberProfile({
        userId: profileTarget.user_id,
        firstName: profileFirstName,
        lastName: profileLastName,
        position: profilePosition,
      })
    },
    onSuccess: async () => {
      setProfileTarget(null)
      setProfileError(null)
      await queryClient.invalidateQueries({ queryKey: ['management-users'] })
    },
    onError: (mutationError: unknown) => {
      setProfileError(mutationError instanceof Error ? mutationError.message : 'Не удалось сохранить профиль')
    },
  })

  const openAccessDialog = (target: ManagementUser) => {
    setAccessTarget(target)
    setAccessReason(target.access_disabled ? 'Доступ восстановлен супер-администратором' : '')
    setAccessError(null)
  }

  const openProfileDialog = (target: ManagementUser) => {
    setProfileTarget(target)
    setProfileFirstName(target.first_name ?? '')
    setProfileLastName(target.last_name ?? '')
    setProfilePosition(target.position ?? '')
    setProfileError(null)
  }

  const runManagementAction = (target: ManagementUser, action: ManagementMemberAction) => {
    if (!confirmManagementAction(target, action)) return
    setManagementNotice(null)
    setManagementError(null)
    managementMutation.mutate({ user: target, action })
  }

  // Фильтрация клиентская: RPC уже отдал весь список (сотни строк), лишние round-trip
  // на каждую букву не нужны, а серверные p_search/p_role_group остаются для будущей пагинации.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (users as ManagementUser[]).filter(u => {
      if (group !== 'all' && u.role_group !== group) return false
      // Несколько отметок сужают выборку (И), а не расширяют: «нет ТГ» + «нет email» = вообще без входа
      if (gaps.length > 0 && !gaps.every(gap => GAP_PREDICATES[gap](u))) return false
      if (accessFilters.length > 0 && !accessFilters.some(value => ACCESS_PREDICATES[value](u))) return false
      if (jobFilters.length > 0 && !jobFilters.some(value => matchesJobFilter(u, value))) return false
      if (royaltyFilters.length > 0 && !royaltyFilters.some(value => matchesRoyaltyFilter(u, value))) return false
      if (!term) return true
      return [
        u.first_name ?? '',
        u.last_name ?? '',
        u.tg_username ?? '',
        u.email ?? '',
        u.phone_number ?? '',
        String(u.telegram_id ?? ''),
        roleLabel(u),
        u.royalty_role ? ROYALTY_ROLE_LABELS[u.royalty_role] : '',
        u.position ?? '',
        u.studio_city_names.join(' '),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term)
    })
  }, [users, group, search, gaps, accessFilters, jobFilters, royaltyFilters])

  // Счётчики на самих чипах: показывают, сколько подпадает под каждый пробел
  // в пределах выбранной группы — иначе непонятно, стоит ли вообще включать фильтр.
  const gapCounts = useMemo(() => {
    const scope = (users as ManagementUser[]).filter(u => group === 'all' || u.role_group === group)
    return {
      no_telegram: scope.filter(GAP_PREDICATES.no_telegram).length,
      no_email: scope.filter(GAP_PREDICATES.no_email).length,
      email_unverified: scope.filter(GAP_PREDICATES.email_unverified).length,
      access_disabled: scope.filter(GAP_PREDICATES.access_disabled).length,
    } as Record<GapFilter, number>
  }, [users, group])

  // Счётчики ролевых фильтров считаем в той же области видимости, что и «пробелы входа»:
  // по выбранной группе и без учёта самих ролевых отметок — иначе включённый фильтр
  // обнулял бы соседние чипы и выбрать второе значение было бы нельзя.
  const mcCounts = useMemo(() => {
    const scope = (users as ManagementUser[]).filter(u => group === 'all' || u.role_group === group)
    const access = Object.fromEntries(
      ACCESS_OPTIONS.map(option => [option.value, scope.filter(ACCESS_PREDICATES[option.value]).length])
    ) as Record<AccessFilter, number>
    const job = Object.fromEntries(
      JOB_OPTIONS.map(option => [option.value, scope.filter(u => matchesJobFilter(u, option.value)).length])
    ) as Record<JobFilter, number>
    const royalty = Object.fromEntries(
      ROYALTY_OPTIONS.map(option => [option.value, scope.filter(u => matchesRoyaltyFilter(u, option.value)).length])
    ) as Record<RoyaltyFilter, number>
    return { access, job, royalty }
  }, [users, group])

  // Флаги супер-админа не загрузились — чип убираем: он показывал бы «0» и читался
  // как «супер-админов нет», хотя на деле их просто не отдали.
  const accessOptions = useMemo(
    () => (superAdminFlagsAvailable ? ACCESS_OPTIONS : ACCESS_OPTIONS.filter(o => o.value !== 'super_admin')),
    [superAdminFlagsAvailable]
  )

  const activeMcFilterCount = accessFilters.length + jobFilters.length + royaltyFilters.length
  const mcFiltersOpen = showMcFilters || activeMcFilterCount > 0

  const resetMcFilters = () => {
    setAccessFilters([])
    setJobFilters([])
    setRoyaltyFilters([])
  }

  const counts = useMemo(() => {
    const result = { withEmail: 0, verified: 0, noLogin: 0 }
    for (const u of users as ManagementUser[]) {
      if (u.email) result.withEmail += 1
      if (u.email_verified_at) result.verified += 1
      if (!u.has_telegram_provider && !u.has_email_provider) result.noLogin += 1
    }
    return result
  }, [users])

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="mx-auto max-w-5xl px-4 py-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold text-foreground">Пользователи</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Все, кто заведён в приложении: контакты, способы входа, роль и последний визит.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex h-9 items-center gap-1.5 self-start rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground hover:bg-muted"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>

        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <button type="button" onClick={() => navigate('/management/users/auth-attempts')} className="text-left">
            <div className="flex h-full items-start gap-3 rounded-md border border-border bg-card p-3 transition-colors hover:bg-accent/50">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <KeyRound className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Попытки авторизации</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Журнал входов: кто, когда, чем закончилось
                </p>
              </div>
            </div>
          </button>

          <button type="button" onClick={() => navigate('/management/users/activity')} className="text-left">
            <div className="flex h-full items-start gap-3 rounded-md border border-border bg-card p-3 transition-colors hover:bg-accent/50">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <Activity className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Активность</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Сессии по неделям и журнал экранов
                </p>
              </div>
            </div>
          </button>

          <button type="button" onClick={() => navigate('/management/health')} className="text-left">
            <div className="flex h-full items-start gap-3 rounded-md border border-border bg-card p-3 transition-colors hover:bg-accent/50">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <HeartPulse className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Здоровье системы</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Нагрузка базы, долгие запросы, ошибки у пользователей
                </p>
              </div>
            </div>
          </button>
        </div>

        <ManagementAccessInbox />

        {/* Результат операции над членством в УК. Без него отказ сервера
            (например «доступ уже не стандартный») исчезал бы молча, и кнопка
            выглядела бы сработавшей. */}
        {(managementNotice || managementError) && (
          <div
            className={`mb-4 rounded-md border p-2 text-sm ${
              managementError
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
          >
            {managementError || managementNotice}
          </div>
        )}

        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Поиск по имени, username, email, телефону, городу, telegram id"
                className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-8 text-sm outline-none focus:border-slate-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                  title="Очистить поиск"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {GROUP_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setGroup(option.value)}
                  className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                    group === option.value
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Проблемы со входом:</span>
              {GAP_OPTIONS.map(option => {
                const active = gaps.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleGap(option.value)}
                    className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                      active
                        ? 'border-fixed-amber-600 bg-fixed-amber-600 text-white'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {option.label}
                    <span className={`ml-1.5 ${active ? 'text-fixed-amber-100' : 'text-muted-foreground/70'}`}>
                      {gapCounts[option.value]}
                    </span>
                  </button>
                )
              })}
              {gaps.length > 0 && (
                <button
                  type="button"
                  onClick={() => setGaps([])}
                  className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted"
                >
                  <X className="h-3.5 w-3.5" />
                  Сбросить
                </button>
              )}
            </div>

            <div className="mt-2 border-t border-border pt-2">
              <button
                type="button"
                onClick={() => {
                  if (mcFiltersOpen && activeMcFilterCount > 0) resetMcFilters()
                  setShowMcFilters(!mcFiltersOpen)
                }}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${mcFiltersOpen ? '' : '-rotate-90'}`} />
                Доступы, должности и роли УК
                {activeMcFilterCount > 0 && (
                  <span className="rounded bg-foreground px-1.5 py-0.5 text-[10px] text-background">
                    {activeMcFilterCount}
                  </span>
                )}
              </button>

              {mcFiltersOpen && (
                <div className="mt-2 grid gap-2">
                  <FilterChipRow
                    label="Доступ УК:"
                    options={accessOptions}
                    active={accessFilters}
                    counts={mcCounts.access}
                    onToggle={value => toggleIn(setAccessFilters, value)}
                  />
                  <FilterChipRow
                    label="Должность:"
                    options={JOB_OPTIONS}
                    active={jobFilters}
                    counts={mcCounts.job}
                    onToggle={value => toggleIn(setJobFilters, value)}
                  />
                  {royaltyRolesAvailable ? (
                    <FilterChipRow
                      label="Роялти:"
                      options={ROYALTY_OPTIONS}
                      active={royaltyFilters}
                      counts={mcCounts.royalty}
                      onToggle={value => toggleIn(setRoyaltyFilters, value)}
                    />
                  ) : (
                    <div className="text-[11px] text-amber-700">
                      Роли в роялти не загрузились — фильтр и бейджи скрыты, чтобы не выдать отказ
                      в доступе за «роли нет». Нужен расширенный доступ УК либо роль в контуре роялти.
                    </div>
                  )}
                  {activeMcFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={resetMcFilters}
                      className="inline-flex h-7 w-fit items-center gap-1 rounded-md px-2 text-[11px] text-muted-foreground hover:bg-muted"
                    >
                      <X className="h-3 w-3" />
                      Сбросить
                    </button>
                  )}
                </div>
              )}
            </div>

            {!isLoading && !error && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span>Показано: {filtered.length} из {users.length}</span>
                <span>С email: {counts.withEmail}</span>
                <span>Email подтверждён: {counts.verified}</span>
                <span>Без привязанного способа входа: {counts.noLogin}</span>
              </div>
            )}
          </div>

          {isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Загружаем пользователей…</div>}

          {error && (
            <div className="p-6 text-center text-sm text-red-600">
              Не удалось загрузить список. Нужна роль супер-администратора и применённая миграция
              <code className="mx-1 rounded bg-red-50 px-1">list_management_users</code>.
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">Никого не нашли по этим условиям.</div>
          )}

          {!isLoading &&
            !error &&
            filtered.map(item => (
              <UserRow
                key={item.user_id}
                user={item}
                currentUserId={user?.id ?? null}
                busy={accessMutation.isPending}
                onAccessChange={openAccessDialog}
                onEditProfile={openProfileDialog}
                onManageRoles={setRolesTarget}
                onManagementAction={runManagementAction}
                managementBusy={managementMutation.isPending}
              />
            ))}
        </div>

        <p className="mt-3 text-[11px] text-muted-foreground">
          «Последний визит» считается по журналу экранов (app_events), который ведётся с 14.07.2026 —
          у тех, кто не заходил после этой даты, будет прочерк.
        </p>
      </div>

      {rolesTarget && (
        <UserRolesDialog
          // Ключ по пользователю: после обновления справочника диалог должен показывать
          // новое состояние ролей, а не значения, снятые при открытии.
          key={rolesTarget.user_id}
          user={users.find(row => row.user_id === rolesTarget.user_id) ?? rolesTarget}
          actor={actorCapabilities}
          isSelf={rolesTarget.user_id === user?.id}
          royaltyRolesAvailable={royaltyRolesAvailable}
          superAdminFlagsAvailable={superAdminFlagsAvailable}
          onClose={() => setRolesTarget(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['management-users'] })}
        />
      )}

      {accessTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg bg-card p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className={`rounded-full p-2 ${accessTarget.access_disabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {accessTarget.access_disabled ? <UserRoundCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
              </div>
              <div>
                <h2 className="text-base font-semibold">
                  {accessTarget.access_disabled ? 'Восстановить доступ' : 'Отключить доступ'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {displayName(accessTarget)}
                </p>
              </div>
            </div>

            <p className="mt-4 text-sm text-muted-foreground">
              {accessTarget.access_disabled
                ? 'Пользователь снова сможет войти и обращаться к данным приложения.'
                : 'Действующие JWT перестанут работать сразу. Студии, роли и владения не изменятся.'}
            </p>

            <label className="mt-4 block text-xs font-medium text-foreground" htmlFor="access-change-reason">
              Причина (обязательно)
            </label>
            <textarea
              id="access-change-reason"
              autoFocus
              value={accessReason}
              onChange={event => {
                setAccessReason(event.target.value)
                setAccessError(null)
              }}
              rows={3}
              maxLength={1000}
              placeholder={accessTarget.access_disabled ? 'Почему доступ восстановлен' : 'Почему доступ отключается'}
              className="mt-1 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <div className="mt-1 text-right text-[11px] text-muted-foreground">{accessReason.trim().length}/1000</div>

            {accessError && <div className="mt-2 rounded bg-red-50 px-3 py-2 text-xs text-red-700">{accessError}</div>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAccessTarget(null)
                  setAccessReason('')
                  setAccessError(null)
                }}
                disabled={accessMutation.isPending}
                className="h-9 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => accessMutation.mutate()}
                disabled={accessMutation.isPending || accessReason.trim().length < 5}
                className={`h-9 rounded-md px-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                  accessTarget.access_disabled ? 'bg-fixed-emerald-600 hover:bg-fixed-emerald-700' : 'bg-fixed-red-600 hover:bg-fixed-red-700'
                }`}
              >
                {accessMutation.isPending
                  ? 'Сохраняем…'
                  : accessTarget.access_disabled ? 'Включить доступ' : 'Отключить доступ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {profileTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg bg-card p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/10 p-2 text-primary">
                <Pencil className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold">Исправить данные сотрудника</h2>
                <p className="mt-1 text-sm text-muted-foreground">{displayName(profileTarget)}</p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block text-xs font-medium text-foreground">
                Имя
                <input
                  autoFocus
                  value={profileFirstName}
                  onChange={event => {
                    setProfileFirstName(event.target.value)
                    setProfileError(null)
                  }}
                  className="mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-slate-500"
                />
              </label>
              <label className="block text-xs font-medium text-foreground">
                Фамилия
                <input
                  value={profileLastName}
                  onChange={event => {
                    setProfileLastName(event.target.value)
                    setProfileError(null)
                  }}
                  className="mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-slate-500"
                />
              </label>
            </div>
            <label className="mt-3 block text-xs font-medium text-foreground">
              Должность <span className="font-normal text-muted-foreground">(необязательно)</span>
              <input
                value={profilePosition}
                onChange={event => {
                  setProfilePosition(event.target.value)
                  setProfileError(null)
                }}
                className="mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-slate-500"
              />
            </label>

            {profileError && <div className="mt-3 rounded bg-red-50 px-3 py-2 text-xs text-red-700">{profileError}</div>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setProfileTarget(null)
                  setProfileError(null)
                }}
                disabled={profileMutation.isPending}
                className="h-9 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => profileMutation.mutate()}
                disabled={profileMutation.isPending || !profileFirstName.trim() || !profileLastName.trim()}
                className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                {profileMutation.isPending ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
